import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon, 
  ChatBubbleLeftRightIcon, 
  HandRaisedIcon,
  CalendarIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const ParentInfoPage = () => {
  const [activeTab, setActiveTab] = useState('communication');

  const communicationGuidelines = [
    {
      title: 'The 24-Hour Rule',
      description: 'Please wait 24 hours after a game or event before discussing concerns with coaches. This allows emotions to settle and promotes more productive conversations.',
      icon: ClockIcon
    },
    {
      title: 'Direct Communication',
      description: 'Address concerns directly with your child\'s coach first. Most issues can be resolved through open, respectful dialogue.',
      icon: ChatBubbleLeftRightIcon
    },
    {
      title: 'Appropriate Topics',
      description: 'Discuss your child\'s development, academic concerns, team rules, and ways to help your child improve.',
      icon: ExclamationCircleIcon
    },
    {
      title: 'Inappropriate Topics',
      description: 'Avoid discussing playing time, team strategy, or other student-athletes. These decisions are left to coaching staff.',
      icon: ExclamationCircleIcon
    }
  ];

  const volunteerOpportunities = [
    {
      role: 'Team Parent',
      description: 'Coordinate team communications, organize team meals, and assist with team events.',
      timeCommitment: '2-3 hours per week',
      skills: 'Organization, communication'
    },
    {
      role: 'Game Day Volunteer',
      description: 'Help with ticket sales, concessions, scorekeeping, or crowd management.',
      timeCommitment: '3-4 hours per game',
      skills: 'Customer service, basic math'
    },
    {
      role: 'Transportation Assistant',
      description: 'Help supervise students during team travel to away games.',
      timeCommitment: 'Varies by game location',
      skills: 'Responsibility, patience'
    },
    {
      role: 'Fundraising Committee',
      description: 'Organize fundraising events and activities to support the athletic program.',
      timeCommitment: '1-2 hours per week',
      skills: 'Event planning, marketing'
    },
    {
      role: 'Equipment Manager',
      description: 'Assist with equipment inventory, maintenance, and distribution.',
      timeCommitment: '2-3 hours per week',
      skills: 'Organization, attention to detail'
    },
    {
      role: 'Social Media Coordinator',
      description: 'Help document games and events, manage team social media presence.',
      timeCommitment: '1-2 hours per week',
      skills: 'Photography, social media'
    }
  ];

  const importantContacts = [
    {
      title: 'Athletic Director',
      name: 'Sarah Johnson',
      email: 'sjohnson@eastendprep.edu',
      phone: '(555) 123-4567',
      office: 'Athletic Office, Room 101'
    },
    {
      title: 'Assistant Athletic Director',
      name: 'Mike Rodriguez',
      email: 'mrodriguez@eastendprep.edu',
      phone: '(555) 123-4568',
      office: 'Athletic Office, Room 102'
    },
    {
      title: 'Athletic Trainer',
      name: 'Dr. Emily Chen',
      email: 'echen@eastendprep.edu',
      phone: '(555) 123-4569',
      office: 'Training Room'
    },
    {
      title: 'Athletic Secretary',
      name: 'Lisa Thompson',
      email: 'athletics@eastendprep.edu',
      phone: '(555) 123-4570',
      office: 'Athletic Office, Main Desk'
    }
  ];

  const tabs = [
    { id: 'communication', name: 'Communication', icon: ChatBubbleLeftRightIcon },
    { id: 'volunteer', name: 'Volunteer Opportunities', icon: HandRaisedIcon },
    { id: 'contacts', name: 'Important Contacts', icon: EnvelopeIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-4">
            <Link to="/family-resources" className="text-blue-200 hover:text-white">
              Family Resources
            </Link>
            <span className="mx-2 text-blue-200">/</span>
            <span className="text-white">Parent Information</span>
          </nav>
          <h1 className="text-4xl font-bold mb-4">
            Parent Information
          </h1>
          <p className="text-xl text-blue-100">
            Important information for parents including communication guidelines and ways to get involved.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Message */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center mb-4">
            <UserGroupIcon className="w-8 h-8 text-primary mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">
              Welcome, Eagle Parents!
            </h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">
            Thank you for supporting your student-athlete's participation in East End Prep athletics. 
            Your involvement and support are crucial to the success of our athletic program.
          </p>
          <p className="text-gray-600 leading-relaxed">
            This page contains important information about how to communicate effectively with coaches, 
            ways to volunteer and support the program, and key contacts within our athletic department.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'border-primary text-primary bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <IconComponent className="w-5 h-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-8">
            {/* Communication Guidelines Tab */}
            {activeTab === 'communication' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  Communication Guidelines
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {communicationGuidelines.map((guideline, index) => {
                    const IconComponent = guideline.icon;
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center mb-3">
                          <IconComponent className="w-6 h-6 text-primary mr-3" />
                          <h4 className="text-lg font-semibold text-gray-900">
                            {guideline.title}
                          </h4>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                          {guideline.description}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-blue-900 mb-3">
                    Best Practices for Parent-Coach Communication
                  </h4>
                  <ul className="space-y-2 text-blue-800">
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></span>
                      Schedule meetings in advance rather than approaching coaches immediately before or after games
                    </li>
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></span>
                      Focus on your child's experience and development, not comparisons to other players
                    </li>
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></span>
                      Be respectful and professional in all communications
                    </li>
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></span>
                      If issues cannot be resolved with the coach, contact the Athletic Director
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Volunteer Opportunities Tab */}
            {activeTab === 'volunteer' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  Volunteer Opportunities
                </h3>
                <p className="text-gray-600 mb-8">
                  We welcome parent volunteers to help make our athletic program successful. 
                  Here are various ways you can contribute your time and talents:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {volunteerOpportunities.map((opportunity, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        {opportunity.role}
                      </h4>
                      <p className="text-gray-600 mb-4">
                        {opportunity.description}
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-600">
                            <strong>Time Commitment:</strong> {opportunity.timeCommitment}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <HandRaisedIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-600">
                            <strong>Skills:</strong> {opportunity.skills}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-green-900 mb-3">
                    Ready to Volunteer?
                  </h4>
                  <p className="text-green-800 mb-4">
                    Contact the Athletic Department to learn more about current volunteer needs 
                    and how to get started. All volunteers must complete a background check.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="mailto:athletics@eastendprep.edu?subject=Volunteer Interest"
                      className="btn-primary inline-flex items-center justify-center"
                    >
                      <EnvelopeIcon className="w-4 h-4 mr-2" />
                      Email Athletic Department
                    </a>
                    <a
                      href="tel:5551234570"
                      className="btn-secondary inline-flex items-center justify-center"
                    >
                      <PhoneIcon className="w-4 h-4 mr-2" />
                      Call (555) 123-4570
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Important Contacts Tab */}
            {activeTab === 'contacts' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  Important Contacts
                </h3>
                <p className="text-gray-600 mb-8">
                  Here are the key contacts within our athletic department. Please don't hesitate 
                  to reach out with questions or concerns.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {importantContacts.map((contact, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {contact.title}
                      </h4>
                      <p className="text-primary font-medium mb-4">
                        {contact.name}
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-3" />
                          <a 
                            href={`mailto:${contact.email}`}
                            className="text-gray-600 hover:text-primary transition-colors duration-200"
                          >
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center">
                          <PhoneIcon className="w-4 h-4 text-gray-400 mr-3" />
                          <a 
                            href={`tel:${contact.phone.replace(/[^0-9]/g, '')}`}
                            className="text-gray-600 hover:text-primary transition-colors duration-200"
                          >
                            {contact.phone}
                          </a>
                        </div>
                        <div className="flex items-start">
                          <svg className="w-4 h-4 text-gray-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-gray-600">
                            {contact.office}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    Office Hours
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600">
                    <div>
                      <p><strong>Monday - Friday:</strong> 7:30 AM - 4:00 PM</p>
                      <p><strong>Game Days:</strong> Extended hours as needed</p>
                    </div>
                    <div>
                      <p><strong>Summer Hours:</strong> 8:00 AM - 3:00 PM</p>
                      <p><strong>Weekends:</strong> By appointment only</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentInfoPage;